import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ImageSlider.css";

type ImageSliderProps = {
    staticContents?: any[]
    images?: string[]
}

export function ImageSlider({ staticContents, images }: ImageSliderProps) {
    const [banners, setBanners] = useState([]);
    const [imageIndex, setImageIndex] = useState(1);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const { t, i18n } = useTranslation();
    const navigate = useHistory();

    useEffect(() => {
        const handleWindowResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleWindowResize);
    });

    useEffect(() => {
        if (staticContents && staticContents.length > 0) {
            const updatedBanners = staticContents.map(staticContent => ({
                alias: staticContent.alias,
                externalAlias: staticContent.custom_specifications?.['external_alias'] ?? null,
                mobileImageUrl: (staticContent?.mobileIcon && staticContent?.mobileIcon[i18n.language]) ?
                    staticContent?.mobileIcon[i18n.language] : null,
                imageUrl: (staticContent?.icon && staticContent?.icon[i18n.language]) ?
                    staticContent?.icon[i18n.language] :
                    (
                        staticContent?.image_url ?
                            staticContent?.image_url[i18n.language] :
                            staticContent?.image?.path
                    )
            }));
            
            // Add clones for infinite loop
            if (updatedBanners.length > 1) {
                const lastBanner = updatedBanners[updatedBanners.length - 1];
                const firstBanner = updatedBanners[0];
                setBanners([lastBanner, ...updatedBanners, firstBanner]);
            } else {
                setBanners(updatedBanners);
            }
        } else if (images && images.length > 0) {
            const imageBanners = images.map((imageUrl, index) => ({
                alias: null,
                externalAlias: null,
                mobileImageUrl: imageUrl,
                imageUrl: imageUrl
            }));
            
            // Add clones for infinite loop
            if (imageBanners.length > 1) {
                const lastBanner = imageBanners[imageBanners.length - 1];
                const firstBanner = imageBanners[0];
                setBanners([lastBanner, ...imageBanners, firstBanner]);
            } else {
                setBanners(imageBanners);
            }
        }

    }, [windowWidth, staticContents, images, i18n.language]);

    useEffect(() => {
        if (banners.length <= 1) return;
        
        const interval = setInterval(() => {
            setImageIndex((index) => index + 1);
        }, 5000);

        return () => clearInterval(interval);
    }, [banners.length]);

    useEffect(() => {
        if (banners.length <= 1) return;
        
        const handleTransitionEnd = () => {
            if (imageIndex >= banners.length - 1) {
                setIsTransitioning(false);
                setImageIndex(1);
            } else if (imageIndex <= 0) {
                setIsTransitioning(false);
                setImageIndex(banners.length - 2);
            }
        };

        if (imageIndex >= banners.length - 1 || imageIndex <= 0) {
            const timeout = setTimeout(() => {
                handleTransitionEnd();
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [imageIndex, banners.length]);

    useEffect(() => {
        if (!isTransitioning) {
            const timeout = setTimeout(() => {
                setIsTransitioning(true);
            }, 50);
            return () => clearTimeout(timeout);
        }
    }, [isTransitioning]);

    function showNextImage() {
        if (banners.length <= 1) return;
        setImageIndex((index) => index + 1);
    }

    function showPrevImage() {
        if (banners.length <= 1) return;
        setImageIndex((index) => index - 1);
    }

    function goToImage(index: number) {
        if (banners.length <= 1) return;
        setImageIndex(index);
    }

    function handleImageClick() {
        const actualBanner = banners.length > 1 ? banners[imageIndex] : banners[0];
        
        if (actualBanner.alias) {
            if (actualBanner.externalAlias) {
                window.open(`${actualBanner.externalAlias}`, "_blank");
                return;
            }
            else {
                navigate.push(`/${i18n.language}/campaign/${actualBanner.alias}`, {
                    state: {
                        alias: actualBanner.alias,
                        staticPage: staticContents
                    }
                });
            }
        }
    }

    return (
        <>
            {banners.length > 0 && (
                <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
                    <div style={{width: "100%", height: "100%", display:"flex" }}>
                        {banners.map((banner, index) => (
                            <img
                                key={`${banner.imageUrl}-${index}`}
                                src={windowWidth < 767 ? banner.mobileImageUrl : banner.imageUrl}
                                className={"img-slider-image"}
                                style={{
                                    translate: `${-100 * imageIndex}%`,
                                    transition: isTransitioning ? "translate 0.5s ease-in-out" : "none",
                                    cursor: banner.alias ? "pointer" : "default"
                                }}
                                onClick={banner.alias ? handleImageClick : undefined}
                            />
                        ))}
                    </div>

                    {banners.length > 1 && (
                        <div style={{
                            position: "absolute",
                            bottom: "20px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            gap: "0.5rem",
                            zIndex: 10
                        }}>
                            {banners.slice(1, -1).map((_, index) => (
                                <button
                                    key={index}
                                    className="img-slider-dot-btn"
                                    onClick={() => goToImage(index + 1)}
                                    aria-label={`Go to image ${index + 1}`}
                                    style={{
                                        color: imageIndex === index + 1 ? 'white' : 'grey'
                                    }}
                                >
                                    ⬤
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
export default ImageSlider;

